import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Camera, CheckCircle2, AlertTriangle, Moon, X, WifiOff, UploadCloud, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { fetchJobPhotos, deleteJobPhoto, rotateJobPhoto } from "../lib/api";
import { resizeImageForUpload } from "../lib/imageResize";
import InAppCamera from "./InAppCamera";
import { enqueuePhoto, removeItem as removeQueuedItem, flush, getPending, subscribe } from "../lib/offlineQueue";

const MIN_PHOTOS = 4;

// Service types where the at-scene photo set is optional (driver can continue
// without taking any). For these jobs there's typically no damage to record
// — photos remain useful if the driver wants them, but never block status.
const OPTIONAL_PHOTO_SERVICES = new Set(["lockout"]);

/**
 * At-scene photos modal — blocks "At Scene" status advancement until
 * either (a) the driver has taken ≥4 photos or (b) they pick an emergency
 * skip reason. Photos upload as they're taken so a partial set survives even
 * if the driver crashes the tab.
 *
 * Props:
 *   open, onOpenChange  — dialog state
 *   job                 — current job
 *   onContinue(skipReason)  — fires when driver confirms (skipReason may be null)
 */
export default function AtScenePhotosModal({ open, onOpenChange, job, onContinue }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const [skipChoice, setSkipChoice] = useState(null);  // "emergency_accident" | "nighttime_too_dark"
  const [rotatingId, setRotatingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // The auto-flusher (reconnect, tab focus, 20s tick, Background Sync) runs
  // outside this component — watch the queue so uploaded photos stop showing
  // as "sending" even when we weren't the ones who sent them.
  useEffect(() => {
    if (!open || !job?.id) return undefined;
    return subscribe(() => { reconcilePending(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.id]);

  // Feb 2026 — hydrate the modal from the job's already-taken photos
  // when it opens. The persistent "Take Photos" button on the driver
  // card can re-open this modal AFTER photos have been dismissed
  // without confirming, so we need to show the driver what they've
  // already got instead of starting from zero (which would tell them
  // "0 taken · Minimum 4 required" even when 3 already exist on the
  // job doc — the badge on the button and the counter here would
  // disagree). Only runs on open transition — mid-session photo state
  // (adds/removes) stays owned by the local `setPhotos`.
  useEffect(() => {
    if (!open) return;
    const server = Array.isArray(job?.photos) ? job.photos : [];
    setPhotos(server);
    // Anything still sitting in the phone's queue for this job counts too —
    // otherwise closing and reopening the modal mid-upload would show fewer
    // photos than the driver has actually taken and block them at the
    // 4-photo minimum.
    getPending()
      .then((items) => {
        const mine = items.filter((it) => it.kind === "photo" && it.jobId === job?.id);
        if (!mine.length) return;
        setPhotos((curr) => [
          ...curr,
          ...mine.map((it) => ({
            id: `offline-${it.id}`, qid: it.id, offline: true,
            previewUrl: URL.createObjectURL(it.blob),
          })),
        ]);
      })
      .catch(() => {});
  }, [open, job?.id]);

  const enough = photos.length >= MIN_PHOTOS;
  const isOptional = job ? OPTIONAL_PHOTO_SERVICES.has(job.service_type) : false;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      // Fallback path (phone's own camera app, when the in-app one can't
      // start). Unlike a frame grabbed from the viewfinder, a camera-app
      // JPEG carries an EXIF rotation flag and can be a 12 MP HEIC — the
      // resizer bakes the rotation in and converts, so these don't arrive
      // sideways or enormous.
      const ready = await resizeImageForUpload(f);
      await stashPhoto(ready, ready.name || f.name);
    }
    setUploading(false);
    // Reset so the same file can be re-picked if needed.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Save the photo on the phone FIRST, then upload in the background.
   *
   * Oct 2026 — this used to upload first and only fall back to the queue
   * once the network error surfaced, with no timeout anywhere in the stack.
   * On one bar the upload doesn't fail, it HANGS, and the camera button was
   * disabled for the whole wait: measured at 25.7s against a stalled
   * connection, where being properly offline took 0.7s. Drivers in fringe
   * coverage were standing at the roadside for minutes.
   *
   * Writing to IndexedDB is instant and always succeeds, so the camera is
   * free again immediately whatever the signal. `flush()` then does the
   * upload on its own time and deletes the local copy on success.
   */
  const stashPhoto = async (blob, filename) => {
    let qid;
    try {
      qid = await enqueuePhoto(job.id, blob, filename);
    } catch {
      toast.error("Couldn't save that photo on the phone — try again.");
      return;
    }
    const previewUrl = URL.createObjectURL(blob);
    setPhotos((curr) => [...curr, { id: `offline-${qid}`, qid, offline: true, previewUrl }]);

    // Background upload — deliberately not awaited.
    flush().then(() => reconcilePending(qid)).catch(() => {});
  };

  /**
   * Swap a locally-held photo for the server's copy once it has uploaded,
   * so the driver can still delete it (removal needs the server-side id)
   * and the "sending" badge doesn't sit there forever. Called after our own
   * flush and whenever the queue changes — the auto-flusher that runs on
   * reconnect lives outside this component, so without the subscription the
   * badges stayed on screen long after the photos had gone.
   */
  const reconcilePending = async (qid) => {
    try {
      const pendingIds = new Set((await getPending()).map((it) => it.id));
      if (qid != null && pendingIds.has(qid)) return;   // not sent yet
      const server = await fetchJobPhotos(job.id);
      setPhotos((curr) => {
        const stillPending = curr.filter((p) => p.offline && pendingIds.has(p.qid));
        curr.forEach((p) => {
          if (p.offline && !pendingIds.has(p.qid) && p.previewUrl) {
            URL.revokeObjectURL(p.previewUrl);
          }
        });
        return [...server, ...stillPending];
      });
    } catch {
      // Leave the local previews alone — they still count and still show.
    }
  };

  // Rotates the stored pixels, so the straightened photo is what the insurer
  // PDF and email attachments carry too — the in-app camera writes no EXIF
  // for anything downstream to honour.
  const rotatePhoto = async (photo) => {
    setRotatingId(photo.id);
    try {
      const r = await rotateJobPhoto(job.id, photo.id, 1);
      setPhotos((curr) =>
        curr.map((p) => (p.id === photo.id ? { ...p, rotated_at: r.rotated_at } : p)),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rotate that photo");
    } finally {
      setRotatingId(null);
    }
  };

  const removePhoto = async (photo) => {
    // Offline-queued photo — pull it out of the phone-side queue.
    if (photo.offline) {
      setPhotos((curr) => curr.filter((p) => p.id !== photo.id));
      try { await removeQueuedItem(photo.qid); } catch { /* already gone */ }
      if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
      return;
    }
    // Optimistic remove — drop from local state immediately, restore on error.
    const prev = photos;
    setPhotos((curr) => curr.filter((p) => p.id !== photo.id));
    try {
      await deleteJobPhoto(job.id, photo.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't remove photo";
      toast.error(detail);
      setPhotos(prev);  // rollback
    }
  };

  const reset = () => {
    setPhotos([]);
    setSkipChoice(null);
  };

  const confirm = () => {
    if (enough) {
      onContinue?.(null);
    } else if (isOptional) {
      // Lockouts etc. — photos optional. If driver took 0, log that they
      // skipped voluntarily; otherwise pass through whatever they have.
      onContinue?.(photos.length === 0 ? "no_damage_to_record" : null);
    } else if (skipChoice) {
      onContinue?.(skipChoice);
    } else {
      toast.error(`Take ${MIN_PHOTOS - photos.length} more photo${MIN_PHOTOS - photos.length === 1 ? "" : "s"} or choose a skip reason.`);
    }
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="max-w-md"
        data-testid="at-scene-photos-modal"
        // The viewfinder is portalled to <body> to get full-bleed, which
        // puts it outside this dialog as far as Radix is concerned — every
        // shutter tap counted as an outside click and closed the modal
        // underneath the driver. Ignore outside interaction while it's up.
        onPointerDownOutside={(e) => { if (cameraOpen) e.preventDefault(); }}
        onInteractOutside={(e) => { if (cameraOpen) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2">
            <Camera className="h-5 w-5 text-amber-600" />
            {isOptional ? "Photos (optional)" : "Take photos before loading"}
          </DialogTitle>
          <DialogDescription>
            {isOptional ? (
              <>Photos are <strong>optional</strong> for this job. Take any if you spot damage or want a record — otherwise just hit continue.</>
            ) : (
              <>Take at least <strong>{MIN_PHOTOS} photos</strong> of the vehicle from all angles (front, back, both sides) before you load it. This is your damage-claim proof.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Big camera trigger */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handleFiles}
            className="hidden"
            data-testid="at-scene-file-input"
          />
          <button
            onClick={() => setCameraOpen(true)}
            disabled={uploading}
            className="w-full h-20 rounded-md bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white transition-colors flex flex-col items-center justify-center gap-1 font-bold disabled:opacity-50"
            data-testid="at-scene-take-photo-btn"
          >
            <Camera className="h-6 w-6" />
            <span>{uploading ? "Saving…" : "Tap to Take Photo"}</span>
          </button>

          {/* The viewfinder lives inside TowHook so nothing lands in the
              driver's gallery. Falls back to the phone's camera only if it
              can't start (permission refused, no camera). */}
          <InAppCamera
            open={cameraOpen}
            count={photos.length}
            jobId={job?.id}
            onCapture={(blob) => stashPhoto(blob, `photo-${Date.now()}.jpg`)}
            onClose={() => setCameraOpen(false)}
            onUnavailable={() => { setCameraOpen(false); fileInputRef.current?.click(); }}
          />

          {/* Progress counter — shows total taken, with a "min 4 required" subtitle
              when the driver is still below the mandatory minimum. */}
          {!(isOptional && photos.length === 0) && (
            <div className="text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
              <div>
                {photos.length} photo{photos.length === 1 ? "" : "s"} taken
                {enough && <CheckCircle2 className="inline h-4 w-4 ml-1.5 text-emerald-600" />}
              </div>
              {!isOptional && !enough && (
                <div className="text-xs font-medium text-amber-700 dark:text-amber-300 mt-0.5">
                  Minimum {MIN_PHOTOS} required ({MIN_PHOTOS - photos.length} to go)
                </div>
              )}
            </div>
          )}

          {/* Thumbnail strip — each thumb has a ✕ button so the driver can
              undo a wrong photo (their thumb, blurry shot, wrong vehicle)
              without dispatcher intervention. */}
          {photos.length > 0 && (
            <div className="grid grid-cols-4 gap-1.5" data-testid="at-scene-thumbs">
              {photos.map((p) => {
                const t = typeof window !== "undefined" ? localStorage.getItem("rescue_token") : null;
                const v = p.rotated_at ? `&v=${encodeURIComponent(p.rotated_at)}` : "";
                const src = p.offline
                  ? p.previewUrl
                  : `${process.env.REACT_APP_BACKEND_URL}${p.url}${t ? `?t=${t}` : "?"}${v}`;
                return (
                  <div key={p.id} className="relative group">
                    <img
                      src={src}
                      alt=""
                      className="aspect-square w-full object-cover rounded border border-slate-200 dark:border-slate-700"
                    />
                    {p.offline && (
                      <div
                        className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-[9px] font-bold text-white text-center py-0.5 rounded-b flex items-center justify-center gap-0.5"
                        title={online
                          ? "Saved on this phone — uploading"
                          : "Saved on this phone — uploads when signal returns"}
                        data-testid={`at-scene-offline-badge-${p.id}`}
                      >
                        {online
                          ? <><UploadCloud className="h-2.5 w-2.5" /> sending</>
                          : <><WifiOff className="h-2.5 w-2.5" /> saved</>}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-rose-300"
                      aria-label="Remove photo"
                      data-testid={`at-scene-remove-${p.id}`}
                    >
                      <X className="h-3 w-3" strokeWidth={3} />
                    </button>
                    {/* Straighten a sideways shot on the spot. Hidden while a
                        photo is still queued on the phone — there is nothing
                        on the server to rotate yet. */}
                    {!p.offline && (
                      <button
                        type="button"
                        onClick={() => rotatePhoto(p)}
                        disabled={rotatingId === p.id}
                        className="absolute -bottom-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-700 hover:bg-slate-800 text-white flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                        aria-label="Rotate photo"
                        title="Rotate a quarter turn"
                        data-testid={`at-scene-rotate-${p.id}`}
                      >
                        <RotateCw className="h-3 w-3" strokeWidth={3} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Emergency skip — only visible if we don't have enough yet AND photos are mandatory */}
          {!enough && !isOptional && (
            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">
                Can&apos;t take photos? Pick a reason:
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSkipChoice("emergency_accident")}
                  className={`p-2 rounded border-2 text-xs font-bold flex flex-col items-center gap-1 ${
                    skipChoice === "emergency_accident"
                      ? "bg-rose-100 border-rose-400 text-rose-800 dark:bg-rose-950/50 dark:border-rose-600 dark:text-rose-200"
                      : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                  }`}
                  data-testid="at-scene-skip-emergency"
                >
                  <AlertTriangle className="h-4 w-4" /> Emergency / Accident
                </button>
                <button
                  onClick={() => setSkipChoice("nighttime_too_dark")}
                  className={`p-2 rounded border-2 text-xs font-bold flex flex-col items-center gap-1 ${
                    skipChoice === "nighttime_too_dark"
                      ? "bg-indigo-100 border-indigo-400 text-indigo-800 dark:bg-indigo-950/50 dark:border-indigo-600 dark:text-indigo-200"
                      : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                  }`}
                  data-testid="at-scene-skip-dark"
                >
                  <Moon className="h-4 w-4" /> Nighttime / Too Dark
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="at-scene-cancel">
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!enough && !isOptional && !skipChoice}
            className={(enough || isOptional) ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"}
            data-testid="at-scene-confirm"
          >
            <CheckCircle2 className="h-4 w-4.5" />
            I&apos;m at scene — continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
